import { useMutation } from "@apollo/client";
import { Button, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { TagPicker } from "components/TagPicker";
import {
  TagPickerFragment,
  TagType,
  WasteLineUnified,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";
import { CheckboxField } from "../../../../../components/Forms/CheckboxField";
import { transform } from "../../../../../hooks/transform/transformWasteLineUnified";
import {
  EDIT_WASTE_LINE_UNIFIED_MUTATION,
  WASTE_LINES_UNIFIED_QUERY,
} from "./api";
import { DeleteLineItemDialog } from "./DeleteLineItemDialog";
import {
  useWasteLineUnifiedInputValidation,
  WasteLineUnifiedFormData,
} from "./validationHooks/useWasteLineUnifiedInputValidation";

interface EditLineItemDialogProps {
  lineItem: Pick<
    WasteLineUnified,
    | "id"
    | "lineItem"
    | "manifestTrackingNumber"
    | "vendorAmount"
    | "shippedDate"
    | "processedManifestId"
    | "willNotReport"
    | "managementMethodCode"
    | "dotDescription"
    | "profileNumber"
    | "eManifestAmount"
    | "isEManifest"
  > & { tags: TagPickerFragment[] };
  onClose: () => void;
}

export const EditLineItemDialog = ({
  lineItem,
  onClose,
}: EditLineItemDialogProps) => {
  const alerts = useAlerts();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [edit, { loading }] = useMutation(EDIT_WASTE_LINE_UNIFIED_MUTATION);

  const defaultValues = useMemo<WasteLineUnifiedFormData>(
    () => ({
      willNotReport: lineItem.willNotReport ?? false,
      lineItem: lineItem.lineItem ?? null,
      dotDescription: lineItem.dotDescription ?? null,
      vendorAmount: lineItem.vendorAmount ?? null,
      eManifestAmount: lineItem.eManifestAmount ?? null,
      vendorProfileNumber: lineItem.profileNumber ?? null,
      vendorManagementMethodCode: lineItem.managementMethodCode ?? null,
      tags: lineItem.tags?.map((t) => ({ id: t.id, name: t.name })) ?? [],
    }),
    [lineItem]
  );

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    issues,
  } = useValidatingForm<WasteLineUnifiedFormData>(
    defaultValues,
    [],
    useWasteLineUnifiedInputValidation()
  );

  const handleEdit = useCallback(
    async (formData: WasteLineUnifiedFormData) => {
      try {
        const input = transform({
          willNotReport: formData.willNotReport,
          lineItem: formData.lineItem ?? "",
          dotDescription: formData.dotDescription ?? "",
          vendorAmount: formData.vendorAmount ?? 0,
          vendorProfileNumber: formData.vendorProfileNumber ?? "",
          vendorManagementMethodCode: formData.vendorManagementMethodCode ?? "",
          tags: formData.tags,
        });
        await edit({
          variables: {
            id: lineItem.id,
            shippedDate: lineItem.shippedDate,
            input,
          },
          refetchQueries: [WASTE_LINES_UNIFIED_QUERY, "WasteTags"],
        });
        alerts.success("Line item updated successfully!");
        onClose();
      } catch (err) {
        alerts.error(`There was an error editing the line item`, err);
      }
    },
    [edit, lineItem.id, lineItem.shippedDate, alerts, onClose]
  );

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        primaryActionButtonProps={{
          onClick: handleSubmit(handleEdit),
          loading,
          disabled: hasCriticalIssues(issues) || !isDirty,
        }}
        title="Edit Manifest Line Item"
        secondaryActionElements={[
          <Button
            key="delete"
            color="error"
            disabled={loading}
            onClick={() => setShowDeleteConfirmDialog(true)}
          >
            Delete Line Item
          </Button>,
        ]}
      >
        <form>
          <Grid container spacing={3}>
            <Grid size={12}>
              <CheckboxField
                name="willNotReport"
                label="Mark as not reportable"
                control={control}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="lineItem"
                label="Line Item Name"
                control={control}
                textFieldProps={{
                  multiline: true,
                  rows: 2,
                }}
              />
            </Grid>
            <Grid size={12}>
              <FormTextField
                name="dotDescription"
                label="DOT Description"
                control={control}
              />
            </Grid>
            <Grid size={6}>
              <FormTextField
                name="vendorAmount"
                label="Amount (Vendor)"
                type="number"
                control={control}
              />
            </Grid>
            <Grid size={6}>
              <FormTextField
                name="eManifestAmount"
                label="Amount (e‑Manifest)"
                control={control}
                disabled
              />
            </Grid>
            <Grid size={6}>
              <FormTextField
                name="vendorProfileNumber"
                label="Profile Number"
                control={control}
              />
            </Grid>
            <Grid size={6}>
              <FormTextField
                name="vendorManagementMethodCode"
                label="Management Method Code"
                control={control}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name="tags"
                control={control}
                render={({ field, fieldState }) => (
                  <TagPicker
                    {...field}
                    {...fieldState}
                    tagType={TagType.Waste}
                    label="Tags"
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </Dialog>
      {showDeleteConfirmDialog && (
        <DeleteLineItemDialog
          lineItem={lineItem}
          onClose={() => setShowDeleteConfirmDialog(false)}
          onDeleteConfirm={() => {
            setShowDeleteConfirmDialog(false);
            onClose();
          }}
        />
      )}
    </>
  );
};
